/**
 * One role — `/countries/:cc/admin/roles/:roleId`. Custom-roles design §1.6.
 *
 * ===========================================================================
 * THE PERMISSION MATRIX, WHICH THE PRODUCT HAS NEVER SHOWN
 * ===========================================================================
 * Grouped BY CONCERN, REUSING `permissionGroups()` — the same grouping the overrides
 * editor ships (`domain/rbac.ts`), so the two screens that describe permissions
 * describe them in the same order with the same headings.
 *
 * ⚠ UPDATED: THE ROW IS NO LONGER THE RAW KEY. It used to be `label={key}` and a
 * font-mono `{key}` span, which asked an administrator to staff a team by reading
 * forty-four dotted strings. Each row now carries the human label, the one-line
 * description, the raw key (still, as its own text node — it is what the server and
 * `role-matrix.json` call this thing) and, for the 22 of the 44 keys that grant more
 * than their name says, the warning that says so. All of it is DATA in
 * `domain/permission-catalog.ts`; see `PermissionRow` at the foot of this file.
 *
 * The grouping used to be MECHANICAL (the prefix before the first dot). It is now the
 * catalogue's `group` field, which is TOTAL BY THE TYPE — a registry key with no
 * catalogue entry does not compile — so a key still cannot silently vanish from this
 * screen, and it can no longer land in a group of one. The argument for the change is
 * recorded at `domain/rbac.ts`'s `permissionGroups()`.
 *
 * ===========================================================================
 * A BUILT-IN: ITS NAME IS READ-ONLY, ITS PERMISSIONS ARE NOT (owner ruling 2026-08-13)
 * ===========================================================================
 * ⚠ CORRECTED. This block used to read "READ-ONLY FOR A BUILT-IN, EDITABLE FOR A
 * COPY … the server answers `SEEDED_ROLE_LOCKED` to every one of those writes". The
 * second half was true and is now false for ONE of those writes: a group admin may
 * replace a built-in role's PERMISSIONS (`PATCH /roles/:id`), because the owner could
 * not otherwise change what his own system's roles may do. Rename and delete are still
 * refused, to everybody.
 *
 * THE RULE ITSELF IS UNCHANGED, AND IT IS WHY THIS FILE MOVES IN THE SAME COMMIT AS THE
 * SERVER: a control the server would refuse is ABSENT rather than dressed up, and a
 * control the server would ACCEPT must not be hidden — "an open API behind a hidden
 * control is a defect wearing a fix's clothes". So on a seeded role the permission
 * checkboxes and Save render for a group admin, the name/description card does not
 * render at all, and the alert says exactly which of the two this is.
 *
 * ⚠ AND THE REQUEST BODY FOLLOWS THE SAME LINE, not just the controls: a save on a
 * seeded role sends `permissions` alone. Sending the unchanged name back would be a
 * rename request the server has to decide about, and a client that relies on the server
 * tolerating a restatement is one normalisation change away from a 403 on a correct
 * button press.
 *
 * ⚠ THE THREE GRP-ONLY KEYS ARE NOT RENDERED AS OPTIONS AT ALL. `config.global`,
 * `dashboard.group` and `remote.access` are granted by no role by design and the
 * server hard-refuses them (`GRP_ONLY_PERMISSION`); offering a checkbox that always
 * fails would be a lie in a form. They are shown, marked, in the read-only view of a
 * seeded role — where they are always absent — so the matrix stays a complete picture
 * of the registry rather than a filtered one.
 *
 * ===========================================================================
 * WHAT THIS SCREEN PROMISES ABOUT TIMING, HONESTLY
 * ===========================================================================
 * Permissions resolve at LOGIN and are embedded in the access token; guards never
 * re-query them. So a grant edit reaches the people holding the role at their next
 * sign-in, not now. The screen says so rather than implying an instant effect.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Button, Card, Checkbox, SkeletonRows, TextField } from '../../components/ui';
import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { ROLE_NAME_MAX_LENGTH, isOverrideOnly, permissionGroups } from '../../domain/rbac';
import { OUT_POWERS_PREFIX, permissionMeta } from '../../domain/permission-catalog';
import { ApiRequestError } from '../../lib/api';
import type { PermissionKey } from '../../lib/permissions';
import { ConfirmDialog } from './ConfirmDialog';
import { RoleWarnings } from './RolesListPage';
import {
  cloneRole,
  deleteRole,
  fetchRoles,
  roleKeys,
  unacknowledgedWarnings,
  updateRole,
} from './roles-api';
import type { RoleDto, RoleWarningDto } from './roles-api';
import { NoAdminAccess, errorSentence } from './states';

function apiSentence(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) return error.message;
  return errorSentence(error, fallback);
}

export function RoleDetailPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { roleId } = useParams<{ roleId: string }>();
  const { can, isGroupAdmin } = useSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const permitted = isGroupAdmin || can(countryCode, 'users.manage');

  const roles = useQuery({
    queryKey: roleKeys.list(),
    queryFn: ({ signal }) => fetchRoles(signal),
    enabled: permitted,
  });

  const role: RoleDto | undefined = useMemo(
    () => (roles.data ?? []).find((entry) => String(entry.id) === roleId),
    [roles.data, roleId],
  );

  /*
   * THE TWO EDIT SURFACES, SPLIT — owner ruling 2026-08-13, mirroring
   * `RolesService.assertSeededWriteAdmissible` field for field. Computed here rather
   * than after the loading guards below because the save mutation reads them: what the
   * screen SHOWS and what the request CARRIES must come from one pair of booleans.
   */
  const seeded = role?.origin === 'seeded';
  /** Permissions: a group admin, on any role — the ruling's whole content. */
  const grantsEditable = isGroupAdmin;
  /** Name and description: a group admin, on a CUSTOM role. Never on a built-in. */
  const identityEditable = isGroupAdmin && !seeded;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<PermissionKey>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<RoleWarningDto[]>([]);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  /*
   * THE FORM FOLLOWS THE SERVER'S ROW — loaded when the id on screen changes, and
   * RE-loaded from the write's own answer in `save.onSuccess` below.
   *
   * ⚠ ADJUSTED DURING RENDER, NOT IN AN EFFECT, AND THE LINT RULE IS RIGHT. An effect
   * that calls setState runs AFTER a paint, so the field would flash empty and then
   * fill — and this repository forbids it (`Calling setState synchronously within an
   * effect can trigger cascading renders`). The React-documented pattern for "reset
   * state when the identity of what is displayed changes" is exactly this: compare with
   * the last id in state and set during render.
   *
   * Rendering `written` rather than what was typed is the discipline the pricing screen
   * set, and it is what makes the server's name normalisation (trim, collapse internal
   * whitespace) VISIBLE instead of leaving the field disagreeing with the database.
   */
  const [loadedId, setLoadedId] = useState<number | null>(null);
  if (role !== undefined && loadedId !== role.id) {
    setLoadedId(role.id);
    setName(role.name);
    setDescription(role.description ?? '');
    setSelected(new Set(role.permissions));
  }

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: roleKeys.root() });
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  /*
   * §1.7 — THE CONFIRM HAPPENS BEFORE THE WRITE, NOT AFTER IT.
   *
   * The design says "the server computes a warnings[] on role create/edit; the UI
   * requires an explicit confirm past them". What shipped rendered them under "Saved —
   * worth checking": true, and delivered after the decision it existed to inform. The
   * server now refuses an unacknowledged composition (422
   * ROLE_WARNINGS_UNACKNOWLEDGED) and hands the sentences back WITH the refusal, so
   * this screen shows exactly what the server said and re-sends the same request.
   *
   * The acknowledgement is a MUTATION ARGUMENT rather than component state, so it can
   * never linger: the button in the dialog is the only thing that can set it, and it
   * sets it for exactly one request. A `const acknowledged` in state would quietly make
   * every later save pre-confirmed.
   */
  const [pendingWarnings, setPendingWarnings] = useState<RoleWarningDto[] | null>(null);

  const save = useMutation({
    mutationFn: (acknowledge: boolean) =>
      updateRole(role?.id ?? 0, {
        // ⚠ NAME AND DESCRIPTION ONLY WHEN THIS SEAT MAY CHANGE THEM. On a built-in they
        // are omitted entirely — see the docblock: the server refuses a rename, and
        // "unchanged" is not a shape a client should ask a server to forgive.
        ...(identityEditable
          ? {
              name: name.trim(),
              description: description.trim() === '' ? null : description.trim(),
            }
          : {}),
        permissions: [...selected],
        ...(acknowledge ? { acknowledge_warnings: true } : {}),
      }),
    onSuccess: (result) => {
      setActionError(null);
      setPendingWarnings(null);
      setWarnings(result.warnings);
      // Re-follow the WRITTEN row: the id has not changed, so the render-time load
      // above will not fire, and the server may have normalised the name.
      setName(result.role.name);
      setDescription(result.role.description ?? '');
      setSelected(new Set(result.role.permissions));
      invalidate();
    },
    onError: (error) => {
      setWarnings([]);
      const unacknowledged = unacknowledgedWarnings(error);
      if (unacknowledged !== null) {
        // NOT an error banner: nothing went wrong and nothing was written. The operator
        // is being asked to read something, which is a dialog, not a red box.
        setActionError(null);
        setPendingWarnings(unacknowledged);
        return;
      }
      setPendingWarnings(null);
      setActionError(apiSentence(error, 'The role was not saved.'));
    },
  });

  const copy = useMutation({
    mutationFn: () => cloneRole(role?.id ?? 0),
    onSuccess: (result) => {
      setActionError(null);
      invalidate();
      navigate(`/countries/${countryCode}/admin/roles/${String(result.role.id)}`);
    },
    onError: (error) => setActionError(apiSentence(error, 'The role was not copied.')),
  });

  const remove = useMutation({
    mutationFn: () => deleteRole(role?.id ?? 0),
    onSuccess: () => {
      setConfirmingDelete(false);
      invalidate();
      navigate(`/countries/${countryCode}/admin/roles`);
    },
    onError: (error) => {
      setConfirmingDelete(false);
      // The server's own sentence carries the COUNT — how many people, or how many
      // notification rules — which is the whole value of the refusal.
      setActionError(apiSentence(error, 'The role was not deleted.'));
    },
  });

  if (!permitted) return <NoAdminAccess />;

  if (roles.isPending) return <SkeletonRows rows={6} label="Loading the role" />;
  if (roles.isError) {
    return (
      <Alert tone="danger" title="The role could not be loaded">
        {apiSentence(roles.error, 'Try again in a moment.')}
      </Alert>
    );
  }
  if (role === undefined) {
    return (
      <Alert tone="danger" title="That role does not exist">
        It may have been deleted.{' '}
        <Link to={`/countries/${countryCode}/admin/roles`} className="underline">
          Back to roles
        </Link>
      </Alert>
    );
  }

  const toggle = (key: PermissionKey): void => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={role.name}
        subtitle={seeded ? 'Built-in role' : 'Custom role'}
        actions={
          isGroupAdmin ? (
            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={() => copy.mutate()} disabled={copy.isPending}>
                Copy
              </Button>
              {seeded ? null : (
                <Button
                  variant="ghost"
                  onClick={() => setConfirmingDelete(true)}
                  disabled={remove.isPending}
                >
                  Delete
                </Button>
              )}
            </div>
          ) : undefined
        }
      />

      {seeded ? (
        <Alert
          tone="info"
          title={
            grantsEditable
              ? 'This is a built-in role: you can change what it can do, but not its name'
              : 'This is a built-in role, so it cannot be changed here'
          }
        >
          {grantsEditable
            ? 'Changing its permissions is kept — the next seed run leaves this role alone ' +
              'from then on, and says so in its output. Its NAME cannot be changed, because ' +
              'the notification rules route to it by name and would stop reaching anybody. ' +
              'Copy it instead if you want a differently named role.'
            : 'Its permissions come from the matrix the system is seeded from. Only a group ' +
              'administrator can change them, and its name cannot be changed at all — the ' +
              'notification rules route to it by name.'}
        </Alert>
      ) : null}

      {actionError === null ? null : (
        <Alert tone="danger" title="That did not work">
          {actionError}
        </Alert>
      )}

      <RoleWarnings warnings={warnings} heading="Saved — worth checking" />

      {identityEditable ? (
        <Card title="Name and description">
          <div className="flex flex-wrap items-end gap-3">
            <TextField
              label="Name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              maxLength={ROLE_NAME_MAX_LENGTH}
            />
            <TextField
              label="Description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={200}
            />
          </div>
          <p className="mt-2 text-label text-fg-secondary">
            Renaming this role also updates any notification rules that route to it by
            name, so nothing stops being delivered.
          </p>
        </Card>
      ) : (
        <Card title="Description">
          <p className="text-body13 text-fg">
            {role.description ?? 'No description.'}
          </p>
        </Card>
      )}

      <Card
        title="What this role can do"
        action={
          <span className="text-label text-fg-secondary">
            {role.permissions.length} of {permissionGroups().reduce((n, g) => n + g.keys.length, 0)}
          </span>
        }
      >
        <div className="flex flex-col gap-4">
          {permissionGroups().map((group) => (
            <div key={group.title} className="flex flex-col gap-1.5">
              <p className="text-label font-medium text-fg">{group.title}</p>
              <ul className="flex flex-col gap-2.5">
                {group.keys.map((key) => (
                  <PermissionRow
                    key={key}
                    permKey={key}
                    held={selected.has(key)}
                    editable={grantsEditable && !isOverrideOnly(key)}
                    overrideOnly={isOverrideOnly(key)}
                    onToggle={() => toggle(key)}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>

        {grantsEditable ? (
          <div className="mt-4 flex items-center gap-3">
            <Button onClick={() => save.mutate(false)} disabled={save.isPending}>
              Save changes
            </Button>
            <span className="text-label text-fg-secondary">
              Takes effect for the {role.holders} {role.holders === 1 ? 'person' : 'people'}{' '}
              who hold this role the next time they sign in.
            </span>
          </div>
        ) : null}
      </Card>

      <Card title="Notifications">
        {/*
          ⚠ STATED, NOT MEASURED — AND THE CUSTOM SENTENCE USED TO OVERSTATE IT.

          It read "No notification rule names this role", as an absolute, from a
          hardcoded string. A review found the state that falsifies it, and it is one
          this system has already been in: `retireRolesNotInMatrix` deletes an unassigned
          seeded row whose `notification_rules` survive (the design cites 16 measured
          silent pairs from retired-Developer rows), the freed NAME is then creatable
          through `POST /roles`, and live rules would route to the new role while this
          card told the operator the opposite — talking them out of the true reading.

          There is no endpoint that answers "which rules name this role?"; the
          reachability surfacing that would make this a MEASUREMENT is R5, a cuttable row
          that was not built. So the sentence now says only what is true by construction,
          names the one way it can be wrong, and says plainly that it did not look.
          `roles-admin.test.tsx` pins it — changing it used to redden nothing, which is
          how the overstatement shipped.
        */}
        <p className="text-body13 text-fg-secondary">
          {seeded
            ? 'Notification rules route to built-in roles by name. Copying this role does not copy its notifications — a copy has a new name, so no rule points at it.'
            : 'Notification routing is seeded, is not editable in the product, and names roles by NAME — so nothing routes to a role added here, unless this name once belonged to a built-in role whose rules outlived it. This card says that from how routing is built; it does not read the routing table.'}
        </p>
      </Card>

      <ConfirmDialog
        open={pendingWarnings !== null}
        title="Save this permission set?"
        body={
          <>
            <span>
              Nothing has been saved yet. This is what these permissions mean for the
              people who hold this role:
            </span>
            <ul className="mt-2 flex flex-col gap-1.5">
              {(pendingWarnings ?? []).map((warning) => (
                <li key={warning.code} className="text-body13 text-fg">
                  {warning.message}
                  <span className="ml-1 font-mono text-mono text-fg-secondary">
                    ({warning.permissions.join(', ')})
                  </span>
                </li>
              ))}
            </ul>
          </>
        }
        confirmLabel="Save anyway"
        busy={save.isPending}
        onConfirm={() => save.mutate(true)}
        onCancel={() => setPendingWarnings(null)}
      />

      <ConfirmDialog
        open={confirmingDelete}
        title={`Delete '${role.name}'?`}
        body={
          role.holders > 0
            ? `${String(role.holders)} person-country assignment(s) still use this role. The server will refuse until they are moved.`
            : 'This cannot be undone. The role is not used by anybody.'
        }
        confirmLabel="Delete role"
        busy={remove.isPending}
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirmingDelete(false)}
      />
    </div>
  );
}

/**
 * ONE permission, described rather than spelled.
 *
 * ===========================================================================
 * WHY THIS EXISTS: THE SCREEN USED TO RENDER THE RAW KEY AND NOTHING ELSE
 * ===========================================================================
 * `label={key}` and a font-mono `{key}` span were the whole row. An administrator
 * staffing a team was choosing between forty-four dotted strings — and two of those
 * strings promise far more than they say. Everything below the checkbox comes from
 * `domain/permission-catalog.ts`; nothing is written here.
 *
 * ⚠ THE RAW KEY IS STILL RENDERED, AS ITS OWN TEXT NODE. It is what the server, the
 * seed matrix and every support conversation call this thing, so replacing it with the
 * human label would make the screen unsearchable against `role-matrix.json`. Label
 * first (what it means), key second (what it is called).
 *
 * ⚠ THE WARNING IS `role='note'`, NOT `role='alert'`. An alert interrupts a screen
 * reader; this text is a standing property of the permission, present on first paint
 * for every out-powering key, not an event. See `permission-labels.test.tsx`.
 */
function PermissionRow({
  permKey,
  held,
  editable,
  overrideOnly,
  onToggle,
}: {
  permKey: PermissionKey;
  held: boolean;
  editable: boolean;
  overrideOnly: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  const meta = permissionMeta(permKey);
  return (
    <li className="flex flex-col gap-0.5">
      {editable ? (
        <Checkbox label={meta.label} checked={held} onChange={onToggle} />
      ) : (
        <span className="text-body text-fg">
          {meta.label}
          {overrideOnly ? null : (
            <span className="text-label text-fg-secondary"> — {held ? 'yes' : 'no'}</span>
          )}
        </span>
      )}
      <p className="text-label text-fg-secondary">{meta.description}</p>
      <p className="flex flex-wrap items-center gap-2 text-label text-fg-secondary">
        <span className="font-mono text-mono">{permKey}</span>
        {overrideOnly ? <span>— never granted by a role; only per person</span> : null}
        {meta.enforcement === 'none' ? <span>— gates nothing today</span> : null}
        {meta.enforcement === 'client-only' ? <span>— hides a button only</span> : null}
      </p>
      {meta.outPowersName === null ? null : (
        <p role="note" className="text-label text-warn-fg">
          {OUT_POWERS_PREFIX}
          {meta.outPowersName}
        </p>
      )}
    </li>
  );
}
